import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { plainTextStyle, body2 } from '@westwing/ui-kit/typography';
import { forTablet } from '@westwing/ui-kit/breakPoints';
import { useFetchingAction } from '../../hooks/useFetchingAction/useFetchingAction';
import { getValuePropsActionsIfNeeded } from '../../redux/valuePropositions/actions';
import { ValueProp } from '../../redux/valuePropositions/types';
import StateInterface from '../../redux/types';
import HybridLink from './Links/HybridLink';
import { LazyHybridImage } from './HybridImage';

export enum Align {
    left = 'LEFT',
    center = 'CENTER',
}

const Container = styled.div<{ align: Align }>`
    ${({ align }) =>
        align === Align.center &&
        css`
            display: flex;
            justify-content: space-around;
            width: 100%;
        `}
`;

const TextWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const PlaceHolderUpper = styled(TextWrapper)`
    background: ${({ theme }) => theme.colors.skeleton01};
    border-radius: 50%;
`;

const Title = styled.p<{ align: Align }>`
    ${body2}
    text-align: left;
    ${forTablet`
        ${plainTextStyle}
    `}
    margin: 0;

    ${({ align }) =>
        align === Align.center &&
        css`
            margin-bottom: auto;
            text-align: center;
        `}
`;

const PropStyles = css<{ align: Align }>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    ${({ align }) =>
        align === Align.center &&
        css`
            flex-direction: column;
            justify-content: flex-start;
            max-width: 115px;
            text-align: center;
        `}
`;

const Prop = styled.div`
    ${PropStyles}
`;

const PropLink = styled(HybridLink)`
    ${PropStyles}
    color: ${({ theme }) => theme.colors.charcoal100};
    :hover {
        opacity: 0.5;
    }
`;

export const PropIcon = styled(LazyHybridImage)<{ align: Align }>`
    height: 40px;
    width: 40px;

    ${({ align }) =>
        align === Align.center &&
        css`
            height: 90px;
            width: 90px;
            display: flex;
            justify-content: center;
            & picture {
                transform: scale(0.62);
            }
        `}
`;

interface OwnProps {
    isFetching: boolean;
    align: Align;
}

export type Props = ValueProp & OwnProps;

const SingleProposition: React.FC<Props> = ({ title, link, imageSrc, isFetching, align }) => {
    const valueProp = (
        <>
            {isFetching ? (
                <PlaceHolderUpper />
            ) : (
                <>
                    <div>
                        <PropIcon
                            align={align}
                            alt={title}
                            desktopSrc={imageSrc}
                            mobileSrc={imageSrc}
                            fallbackSrc={imageSrc}
                        />
                    </div>
                    <TextWrapper>
                        <Title align={align}>{title}</Title>
                    </TextWrapper>
                </>
            )}
        </>
    );

    if (link) {
        return (
            <PropLink to={link} testId="value_prop_link" align={align}>
                {valueProp}
            </PropLink>
        );
    }
    return (
        <Prop data-testid="value_prop_normal" align={align}>
            {valueProp}
        </Prop>
    );
};

const ValueProps: React.FC<{ className?: string; align: Align }> = ({ className, align }) => {
    useFetchingAction(getValuePropsActionsIfNeeded());
    const valueProps = useSelector<StateInterface, ValueProp[] | undefined>(state => state.valueProps.valueProps) || [];
    const fetchingValueProps = useSelector<StateInterface, boolean>(state => state.valueProps.isFetching);
    const hasError = useSelector<StateInterface, boolean>(state => state.valueProps.didInvalidate);

    if (hasError) {
        return <></>;
    }
    return (
        <Container align={align} className={className} data-testid="valuePropsContainer">
            {valueProps.map(valueProp => (
                <SingleProposition align={align} isFetching={fetchingValueProps} key={valueProp.title} {...valueProp} />
            ))}
        </Container>
    );
};

export default ValueProps;
